import { useCallback, useEffect, useId, useState } from 'react';
import {
  USAGE_ALLOWED_TYPES,
  type Asset,
  type AssetType,
  type ProjectAssetUsage,
} from '@gc/shared';
import { ApiError } from '../../api/client.js';
import { listarAssets, subirAsset } from '../../api/assets.js';

export type SelectorAssetProps = {
  readonly projectId: string;
  /** Determina qué tipos se ofrecen: un audio no puede ser el visual de una escena. */
  readonly usage: ProjectAssetUsage;
  readonly seleccionado: string | null;
  readonly deshabilitado?: boolean;
  /** Texto del campo de subida. Sin él, no se ofrece subir desde aquí. */
  readonly etiquetaSubida?: string;
  /** Restringe los tipos por debajo de lo que admite el uso (p. ej. solo IMAGE). */
  readonly tiposPermitidos?: readonly AssetType[];
  onSeleccionar(assetId: string | null): void;
};

type Carga =
  | { readonly tipo: 'cargando' }
  | { readonly tipo: 'listo'; readonly assets: Asset[] }
  | { readonly tipo: 'error'; readonly mensaje: string };

const ETIQUETA_TIPO: Record<AssetType, string> = {
  IMAGE: 'Imagen',
  VIDEO: 'Vídeo',
  VOICE: 'Voz',
  MUSIC: 'Música',
  AUDIO: 'Audio',
  STORYBOARD: 'Storyboard',
  SCRIPT: 'Guion',
  RENDER: 'Render',
};

/** Tipos MIME que el navegador propone en el diálogo, según lo que admite el uso. */
const ACEPTA: Record<AssetType, string> = {
  IMAGE: 'image/png,image/jpeg,image/webp',
  VIDEO: 'video/mp4',
  VOICE: 'audio/mpeg,audio/wav,audio/aac,audio/mp4',
  MUSIC: 'audio/mpeg,audio/wav,audio/aac,audio/mp4',
  AUDIO: 'audio/mpeg,audio/wav,audio/aac,audio/mp4',
  STORYBOARD: '',
  SCRIPT: '',
  RENDER: '',
};

function detalle(asset: Asset): string {
  if (asset.metadata.width !== undefined) return `${asset.metadata.width}×${asset.metadata.height}`;
  if (asset.metadata.durationMs !== undefined) return `${Math.round(asset.metadata.durationMs / 1000)} s`;
  return '';
}

/** Miniatura según el tipo real; el audio se escucha ahí mismo. */
function Miniatura({ asset }: { asset: Asset }): React.JSX.Element {
  if (asset.type === 'IMAGE') {
    return <img src={asset.contentUrl} alt="" loading="lazy" style={{ width: '100%', height: '6rem', objectFit: 'cover', display: 'block' }} />;
  }
  if (asset.type === 'VIDEO' || asset.type === 'RENDER') {
    return <video src={asset.contentUrl} preload="metadata" muted style={{ width: '100%', height: '6rem', objectFit: 'cover', display: 'block', background: '#000' }} />;
  }
  return (
    <div style={{ height: '6rem', display: 'grid', placeItems: 'center', background: 'var(--fondo-hundido)' }}>
      <span aria-hidden="true" style={{ fontSize: '1.75rem' }}>♪</span>
    </div>
  );
}

/**
 * Biblioteca visual para elegir un archivo para un uso concreto. Muestra
 * miniaturas (no nombres de archivo), permite buscar, acotar al proyecto y
 * subir un archivo nuevo en el mismo sitio. Elegir o subir es la misma acción
 * para el usuario, así que viven juntas; la subida pasa por el endpoint de la
 * biblioteca (validación por firma, límites y asociación).
 */
export function SelectorAsset({
  projectId,
  usage,
  seleccionado,
  deshabilitado,
  etiquetaSubida,
  tiposPermitidos,
  onSeleccionar,
}: SelectorAssetProps): React.JSX.Element {
  const [carga, setCarga] = useState<Carga>({ tipo: 'cargando' });
  const [abierto, setAbierto] = useState(false);
  const [soloProyecto, setSoloProyecto] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [subiendo, setSubiendo] = useState<number | null>(null);
  const [errorSubida, setErrorSubida] = useState<string | null>(null);
  const idSubida = useId();
  const idGaleria = useId();

  const permitidos = tiposPermitidos ?? USAGE_ALLOWED_TYPES[usage];

  const cargar = useCallback(async () => {
    try {
      // Se piden los tipos compatibles por separado: el filtro de la API acepta
      // un tipo por consulta, y así el selector nunca ofrece algo inválido.
      const paginas = await Promise.all(
        permitidos.map((type) => listarAssets({ type, limit: 50, ...(soloProyecto ? { projectId } : {}) })),
      );
      const assets = paginas.flatMap((pagina) => pagina.data);
      assets.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setCarga({ tipo: 'listo', assets });
    } catch (causa) {
      setCarga({ tipo: 'error', mensaje: causa instanceof ApiError ? causa.message : 'No se pudo cargar la biblioteca.' });
    }
  }, [permitidos, projectId, soloProyecto]);

  useEffect(() => {
    setCarga({ tipo: 'cargando' });
    void cargar();
  }, [cargar]);

  async function subir(archivo: File): Promise<void> {
    setSubiendo(0);
    setErrorSubida(null);
    try {
      const asset = await subirAsset(projectId, archivo, {
        onProgreso: ({ enviados, total }) => setSubiendo(total === 0 ? 0 : Math.round((enviados / total) * 100)),
      });
      await cargar();
      // Lo recién subido queda elegido: es lo que el usuario quería usar.
      onSeleccionar(asset.id);
      setAbierto(false);
    } catch (causa) {
      setErrorSubida(causa instanceof ApiError ? causa.message : 'No se pudo subir el archivo.');
    } finally {
      setSubiendo(null);
    }
  }

  const bloqueado = deshabilitado === true || subiendo !== null;
  const acepta = [...new Set(permitidos.map((t) => ACEPTA[t]).filter((v) => v !== ''))].join(',');
  const assets = carga.tipo === 'listo' ? carga.assets : [];
  const actual = assets.find((a) => a.id === seleccionado) ?? null;
  const filtrados = assets.filter((a) => {
    const q = busqueda.trim().toLowerCase();
    return q === '' || a.filename.toLowerCase().includes(q) || a.tags.some((t) => t.toLowerCase().includes(q));
  });

  return (
    <div className="selector-asset">
      <p style={{ margin: '0 0 0.35rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={bloqueado}
          aria-expanded={abierto}
          aria-controls={idGaleria}
          onClick={() => setAbierto((v) => !v)}
        >
          {abierto ? 'Cerrar biblioteca' : seleccionado === null ? 'Elegir de la biblioteca' : 'Cambiar'}
        </button>
        {seleccionado !== null && (
          <>
            <small>
              {actual ? `${actual.filename} (${detalle(actual)})` : 'archivo seleccionado'}
            </small>
            <button type="button" disabled={bloqueado} onClick={() => onSeleccionar(null)}>
              Quitar
            </button>
          </>
        )}
        {seleccionado === null && <small>— sin asignar —</small>}
      </p>

      <div id={idGaleria} hidden={!abierto} style={{ border: '1px solid var(--borde)', borderRadius: 'var(--radio)', padding: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          <label style={{ fontSize: '0.85rem' }}>
            <input type="checkbox" checked={soloProyecto} onChange={(e) => setSoloProyecto(e.target.checked)} disabled={bloqueado} />{' '}
            Solo archivos de este proyecto
          </label>
          <input
            type="search"
            aria-label="Buscar en la biblioteca"
            placeholder="Buscar por nombre o etiqueta"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ maxWidth: '16rem' }}
          />
          <small>{permitidos.map((t) => ETIQUETA_TIPO[t]).join(' / ')}</small>
        </div>

        {carga.tipo === 'cargando' && <p role="status">Cargando archivos…</p>}
        {carga.tipo === 'error' && <p role="alert">{carga.mensaje}</p>}

        {carga.tipo === 'listo' && (
          <ul
            role="listbox"
            aria-label="Archivos de la biblioteca"
            style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(9rem, 1fr))', gap: '0.5rem' }}
          >
            {filtrados.map((asset) => {
              const elegido = asset.id === seleccionado;
              return (
                <li key={asset.id} role="option" aria-selected={elegido}>
                  <button
                    type="button"
                    disabled={bloqueado}
                    aria-label={`${asset.filename} (${ETIQUETA_TIPO[asset.type]}${detalle(asset) ? `, ${detalle(asset)}` : ''})`}
                    onClick={() => {
                      onSeleccionar(elegido ? null : asset.id);
                      if (!elegido) setAbierto(false);
                    }}
                    style={{
                      width: '100%', padding: 0, overflow: 'hidden', textAlign: 'left',
                      border: elegido ? '2px solid var(--acento)' : '1px solid var(--borde)',
                    }}
                  >
                    <Miniatura asset={asset} />
                    <span style={{ display: 'block', padding: '0.25rem 0.4rem', fontSize: '0.75rem', color: 'var(--texto-suave)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {asset.filename}
                      <br />
                      {ETIQUETA_TIPO[asset.type]} · {detalle(asset)}
                    </span>
                  </button>
                  {(asset.type === 'AUDIO' || asset.type === 'MUSIC' || asset.type === 'VOICE') && (
                    <audio src={asset.contentUrl} controls preload="none" style={{ width: '100%', marginTop: '0.25rem' }} />
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {carga.tipo === 'listo' && filtrados.length === 0 && (
          <p>
            <small>
              {busqueda.trim() !== ''
                ? 'Nada coincide con la búsqueda.'
                : `No hay archivos compatibles (${permitidos.map((t) => ETIQUETA_TIPO[t]).join(' o ')})${soloProyecto ? ' en este proyecto; desmarca el filtro para ver toda la biblioteca' : ''}.`}
            </small>
          </p>
        )}

        {errorSubida !== null && <p role="alert">{errorSubida}</p>}
      </div>

      {/* La subida siempre está a mano, con la galería abierta o cerrada. */}
      {etiquetaSubida !== undefined && (
        <p style={{ margin: '0.25rem 0 0' }}>
          <label htmlFor={idSubida} style={{ fontSize: '0.85rem' }}>
            {etiquetaSubida}
          </label>
          <br />
          <input
            id={idSubida}
            type="file"
            accept={acepta}
            disabled={bloqueado}
            onChange={(evento) => {
              const archivo = evento.target.files?.[0];
              evento.target.value = '';
              if (archivo !== undefined) void subir(archivo);
            }}
          />
          {subiendo !== null && <span role="status"> Subiendo… {subiendo} %</span>}
          {errorSubida !== null && <span role="alert"> {errorSubida}</span>}
        </p>
      )}
    </div>
  );
}
